import React from 'react';
import styled from 'styled-components';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import SecondaryProductCard from '@99designs/apps/spa/common/Product/ProductCard/SecondaryProductCard';
import giftLady from '@99designs/apps/spa/images/laptop-lady.svg';
import wandLady from '@99designs/apps/spa/images/web-onboarder/wand-lady-and-browser.svg';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { isFixedPriceFragment } from '@99designs/apps/spa/pages/create/util/filterProducts';
import { parseQueryString } from '@99designs/apps/spa/pages/create/util/queryString';
import { PartnerLogo } from '@99designs/brief';
import { Box, Button, SmallPageHeading, Stack } from '@99designs/emu';
import { colors, typography } from '@99designs/emu/settings';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { linkTo } from '@99designs/routing';
import { WixLogo } from '@99designs/spa-common';
import { Desktop, InlinedSvg } from '@99designs/spa-common';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';
import { Partner } from '@99designs/spa-common';

const RightIllustration = styled(InlinedSvg)`
    width: 450px;
    position: absolute;
    bottom: 16px;
    right: -280px;
`;

const LeftIllustration = styled(InlinedSvg)`
    position: absolute;
    width: 450px;
    top: 112px;
    left: -384px;
`;

const CardContainer = styled.div`
    > * {
        background-color: white;
    }
`;

const Attribution = styled.div`
    position: absolute;
    bottom: -10px;
    right: -200px;
    font-family: ${typography.fontFamilies.attribution};
    color: ${colors.grey700};
    font-style: italic;
    font-size: ${typography.typeScale.size2}px;
`;

export default function WixCreate() {
    const { error, data, loading } = useProductsQuery({
        variables: { input: { categories: ['wix-website'] } },
    });

    // eslint-disable-next-line no-restricted-globals
    const { designerId } = parseQueryString(location.search);

    if (error) {
        throw error;
    }

    if (loading || !data) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    return (
        <ContentSetting>
            <Box marginBottom="1.5bu">
                <PartnerLogo partner={Partner.Wix} />
            </Box>
            <Box marginTop={['none', '2bu']} position="relative">
                <Desktop>
                    <LeftIllustration>{giftLady}</LeftIllustration>
                    <RightIllustration>{wandLady}</RightIllustration>
                    <Attribution>
                        <FormattedMessage
                            message={__placeholder('illustrations by {designerName}')}
                            values={{ designerName: 'wildanya' }}
                        />
                    </Attribution>
                </Desktop>
                <Stack space="1.5bu">
                    {data.products.map((p) => {
                        if (!isFixedPriceFragment(p)) {
                            return null;
                        }

                        const packageForProductSummary = {
                            title: p.title,
                            description: p.description,
                            amountInCents: p.pricing.amount.amountInCents,
                            currency: p.pricing.amount.currency,
                        };

                        return (
                            <CardContainer key={p.key}>
                                <ProductCard
                                    rightColumn={
                                        <Stack space="1bu">
                                            <Box display="flex" justifyContent="center">
                                                <ViewProductDetails
                                                    features={p.details}
                                                    additionalDetails={p.additionalDetails}
                                                    productSummary={packageToProductSummary({
                                                        ...packageForProductSummary,
                                                        logo: WixLogo,
                                                    })}
                                                    image={p.image}
                                                    headerVideo={p.headerVideo}
                                                />
                                            </Box>
                                            <ButtonWrapper>
                                                <Button
                                                    size="large"
                                                    appearance="primary"
                                                    width="fill"
                                                    href={linkTo.briefCreate(p.key)}
                                                    type="button"
                                                >
                                                    {__('Start brief')}
                                                </Button>
                                            </ButtonWrapper>
                                        </Stack>
                                    }
                                    productSummary={packageToProductSummary(
                                        packageForProductSummary
                                    )}
                                    features={p.details}
                                    hasLeftBorder={false}
                                />
                            </CardContainer>
                        );
                    })}
                </Stack>
            </Box>
            <Box marginTop="2bu">
                <Stack space="1.5bu">
                    <Stack space="0.5bu">
                        <SmallPageHeading>
                            {__('Don’t quite see what you’re looking for?')}
                        </SmallPageHeading>
                        <div>
                            {__(
                                'Our design professionals can provide quotes for simple updates to your current Wix website. Or see below for more custom project options to get your website humming.'
                            )}
                        </div>
                    </Stack>
                    <SecondaryProductCard
                        title={__('Custom Wix Online project')}
                        description={__(
                            'Get tailored quotes from up to 5 designers based on your needs. Through a custom project, you can: create a Wix website from scratch with a professional to guide you; migrate your website to Wix so it’s all in one place; or redesign your current Wix website with a fresh lick of paint from our professional designers.'
                        )}
                        cta={
                            <ButtonWrapper>
                                <Button
                                    size="large"
                                    appearance="secondary"
                                    width="fill"
                                    href={linkTo.briefCreate('wix-custom', designerId)}
                                    type="button"
                                >
                                    {__('Start brief')}
                                </Button>
                            </ButtonWrapper>
                        }
                    />
                </Stack>
            </Box>
        </ContentSetting>
    );
}
